import api from './api';

export interface Organization {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
}

export interface Site {
  id: string;
  organization_id: string;
  name: string;
  address?: string;
  is_active: boolean;
  created_at: string;
}

export interface Logger {
  id: string;
  site_id: string;
  name: string;
  serial_number: string;
  is_active: boolean;
  battery_level?: number;
  last_seen_at?: string;
  created_at: string;
  probes?: Probe[];
}

export interface Probe {
  id: string;
  logger_id: string;
  name: string;
  position?: number;
  measurement_type: string;
  unit: string;
  warning_low?: number;
  warning_high?: number;
  alarm_low?: number;
  alarm_high?: number;
  is_active: boolean;
  created_at: string;
  // Fields from ProbeWithLastMeasurement (included when probes come nested in logger response)
  last_value?: number | string;
  last_measured_at?: string;
  last_probe_status?: number;  // firmware bitfield: 0x10=UNCALIBRATED, 0x20=CERT_EXPIRED, etc.
  ack_flags?: number;          // ack bitfield: 0x01=UNCALIBRATED, 0x02=CERT_EXPIRED
}

export interface Measurement {
  id: number;
  probe_id: string;
  timestamp: string;
  value_raw: number;
  value_calibrated: number;
  unit: string;
}

export interface MeasurementStats {
  min: number | null;
  max: number | null;
  avg: number | null;
  std_dev: number | null;
  amplitude: number | null;
  count: number;
}

export interface MeasurementListResponse {
  items: Measurement[];
  total: number;
  skip: number;
  limit: number;
  stats: MeasurementStats;
}

export type AlertType = 'alarm_low' | 'alarm_high' | 'warning_low' | 'warning_high' | 'offline' | 'battery_low';
export type AlertSeverity = 'warning' | 'critical';

export interface Alert {
  id: string;
  probe_id: string;
  alert_type: AlertType;
  severity: AlertSeverity;
  threshold_value?: number;
  measured_value?: number;
  message?: string;
  triggered_at: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  acknowledge_note?: string;
  resolved_at?: string;
  notification_sent_at?: string;
  notification_error?: string;
  escalation_sent_at?: string;
  created_at: string;
  updated_at: string;
  probe_name?: string;
  logger_name?: string;
  site_name?: string;
  organization_name?: string;
}

export interface AlertListResponse {
  items: Alert[];
  total: number;
  skip: number;
  limit: number;
  active_count: number;
  critical_count: number;
}

export interface AlertStats {
  total_active: number;
  total_acknowledged: number;
  total_resolved_24h: number;
  by_severity: Record<string, number>;
  by_type: Record<string, number>;
  by_site: Array<{ site_name: string; count: number }>;
}

export interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ApiKeyCreated extends ApiKey {
  key: string;
  message: string;
}

export const dataService = {
  // Organizations
  async getOrganizations(): Promise<Organization[]> {
    const response = await api.get('/organizations');
    return response.data;
  },

  async getOrganization(id: string): Promise<Organization> {
    const response = await api.get(`/organizations/${id}`);
    return response.data;
  },

  // Sites
  async getSites(organizationId?: string): Promise<Site[]> {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await api.get('/sites', { params });
    return response.data.items || response.data;
  },

  async getSite(id: string): Promise<Site> {
    const response = await api.get(`/sites/${id}`);
    return response.data;
  },

  // Loggers
  async getLoggers(siteId?: string): Promise<Logger[]> {
    const params = siteId ? { site_id: siteId } : {};
    const response = await api.get('/loggers', { params });
    return response.data.items || response.data;
  },

  async getLogger(id: string): Promise<Logger> {
    const response = await api.get(`/loggers/${id}`);
    return response.data;
  },

  // Probes
  async getProbes(loggerId?: string): Promise<Probe[]> {
    const params = loggerId ? { logger_id: loggerId } : {};
    const response = await api.get('/probes', { params });
    return response.data.items || response.data;
  },

  async getProbe(id: string): Promise<Probe> {
    const response = await api.get(`/probes/${id}`);
    return response.data;
  },

  // Measurements
  async getMeasurements(
    probeId: string,
    params?: {
      start?: string;
      end?: string;
      limit?: number;
    }
  ): Promise<Measurement[]> {
    const response = await api.get('/measurements', {
      params: {
        probe_id: probeId,
        ...params,
      },
    });
    return response.data.items || response.data;
  },

  async getMeasurementsWithStats(
    probeId: string,
    params?: {
      start?: string;
      end?: string;
      limit?: number;
    }
  ): Promise<MeasurementListResponse> {
    const response = await api.get('/measurements', {
      params: {
        probe_id: probeId,
        ...params,
      },
    });
    return response.data;
  },

  async getMeasurementsOfficial(
    probeId: string,
    params?: {
      start?: string;
      end?: string;
      limit?: number;
    }
  ): Promise<MeasurementListResponse> {
    const response = await api.get('/measurements/official', {
      params: {
        probe_id: probeId,
        ...params,
      },
    });
    return response.data;
  },

  async getExportUrl(
    loggerId: string,
    format: 'pdf' | 'csv',
    params: { probe_id: string; start: string; end: string }
  ): Promise<string> {
    const qs = new URLSearchParams({
      probe_id: params.probe_id,
      start: params.start,
      end: params.end,
    }).toString();
    // Return the full URL for direct download via fetch/blob
    return `${api.defaults.baseURL}/loggers/${loggerId}/report/${format}?${qs}`;
  },

  // Alerts
  async getAlerts(params?: {
    status?: 'active' | 'acknowledged' | 'resolved';
    severity?: 'warning' | 'critical';
    probe_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<AlertListResponse> {
    const response = await api.get('/alerts', { params });
    return response.data;
  },

  async getAlert(id: string): Promise<Alert> {
    const response = await api.get(`/alerts/${id}`);
    return response.data;
  },

  async acknowledgeAlert(id: string, note?: string): Promise<Alert> {
    const response = await api.post(`/alerts/${id}/acknowledge`, { note });
    return response.data;
  },

  async resolveAlert(id: string): Promise<Alert> {
    const response = await api.post(`/alerts/${id}/resolve`);
    return response.data;
  },

  async getAlertStats(): Promise<AlertStats> {
    const response = await api.get('/alerts/stats');
    return response.data;
  },

  // API Keys
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await api.get('/api-keys');
    return response.data;
  },

  async createApiKey(name: string): Promise<ApiKeyCreated> {
    const response = await api.post('/api-keys', { name });
    return response.data;
  },

  async revokeApiKey(id: string): Promise<void> {
    await api.delete(`/api-keys/${id}`);
  },
};
